// meetingRooms/hooks/useGeneralInfo.js
import { useState, useRef } from 'react';
import { updateResourceManagement } from 'api';

export const useGeneralInfo = (allMeetingRooms, showSnackbar) => {
    const [generalInfoFormData, setGeneralInfoFormData] = useState({});
    const [generalInfoHasChanges, setGeneralInfoHasChanges] = useState({});
    const [isSavingGeneralInfo, setIsSavingGeneralInfo] = useState(false);
    const isSavingRef = useRef(false);

    // Handle form changes for general info
    const handleGeneralInfoFormChange = (meetingRoomSlug, newFormData) => {
        setGeneralInfoFormData(prev => ({
            ...prev,
            [meetingRoomSlug]: newFormData
        }));

        // Track that there are unsaved changes
        setGeneralInfoHasChanges(prev => ({
            ...prev,
            [meetingRoomSlug]: true
        }));
    };

    // Save general info for a specific meeting room
    const handleSaveGeneralInfo = async (meetingRoomSlug) => {
        const formData = generalInfoFormData[meetingRoomSlug];
        
        if (!formData) {
            showSnackbar("No changes to save", "info");
            return;
        }

        try {
            setIsSavingGeneralInfo(true);
            isSavingRef.current = true;

            const meetingRoom = allMeetingRooms.find(mr => mr.slug === meetingRoomSlug);
            if (!meetingRoom) {
                throw new Error("Meeting room not found");
            }

            // Prepare data for API submission
            const requestData = {
                resource: {
                    slug: meetingRoom.slug, // Make sure we include the slug
                    id: meetingRoom.id, // Still need the ID for the API
                    name: formData.name,
                    description: formData.description,
                    capacity: formData.capacity,
                    status: formData.status
                }
            };

            const result = await updateResourceManagement(requestData, meetingRoomSlug);

            if (!result || !result.success) {
                throw new Error(result?.error || 'Failed to update meeting room');
            }

            // Clear the "has changes" flag for this meeting room
            setGeneralInfoHasChanges(prev => ({
                ...prev,
                [meetingRoomSlug]: false
            }));

            showSnackbar('General information updated successfully', 'success');

        } catch (error) {
            console.error('Error updating general info:', error);
            showSnackbar('Failed to update general information: ' + error.message, 'error');
        } finally {
            setIsSavingGeneralInfo(false);
            isSavingRef.current = false;
        }
    };

    // Cancel general info changes
    const handleCancelGeneralInfo = (meetingRoomSlug) => {
        // Find the original data for this meeting room
        const originalMeetingRoom = allMeetingRooms.find(mr => mr.slug === meetingRoomSlug);
        
        if (originalMeetingRoom) {
            // Reset form data to original values
            setGeneralInfoFormData(prev => ({
                ...prev,
                [meetingRoomSlug]: {
                    name: originalMeetingRoom.name || '',
                    description: originalMeetingRoom.description || '',
                    capacity: originalMeetingRoom.capacity || 1,
                    status: originalMeetingRoom.status || 'IN'
                }
            }));
        }

        // Clear the "has changes" flag
        setGeneralInfoHasChanges(prev => ({
            ...prev,
            [meetingRoomSlug]: false
        }));

        showSnackbar('Changes cancelled', 'info');
    };

    // Initialize general info data from meeting rooms
    const initializeGeneralInfo = (meetingRooms) => {
        const newGeneralInfoFormData = {};
        const newGeneralInfoHasChanges = {};

        meetingRooms.forEach(meetingRoom => {
            const generalInfoData = {
                name: meetingRoom.name || '',
                description: meetingRoom.description || '',
                capacity: meetingRoom.capacity || 1,
                status: meetingRoom.status || 'IN',
                slug: meetingRoom.slug,
                canBeActive: true // TODO: Implement rules for this later
            };

            // Use slug as key, not ID
            newGeneralInfoFormData[meetingRoom.slug] = generalInfoData;
            newGeneralInfoHasChanges[meetingRoom.slug] = false; // Always start fresh
        });

        setGeneralInfoFormData(newGeneralInfoFormData);
        setGeneralInfoHasChanges(newGeneralInfoHasChanges);
    };

    return {
        generalInfoFormData,
        generalInfoHasChanges,
        isSavingGeneralInfo,
        isSavingRef,
        handleGeneralInfoFormChange,
        handleSaveGeneralInfo,
        handleCancelGeneralInfo,
        initializeGeneralInfo
    };
};